import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, onSnapshot, orderBy, query, writeBatch } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { loadPdf, renderPage, type PdfDoc } from './pdf';
import FieldBox from './FieldBox';
import { apply, canRedo, canUndo, commit, initHistory, redo, undo, type HistoryState } from './history';
import {
  CORE_FIELD_TYPES, EXTENDED_FIELD_TYPES, FIELD_SPECS, VALIDATION_KINDS, VALIDATION_LABELS,
  align, clampToPage, defaultSizeFor, distribute, overlaps, roleColor, snap,
  type AlignMode, type DistributeMode, type DocumentRecord, type Field, type FieldType,
  type NormRect, type SnapGuide, type ValidationKind,
} from '@beckersign/shared';

interface Role { roleId: string; name: string }
interface Props { envelopeId: string; documents: DocumentRecord[]; roles: Role[]; readOnly: boolean }

const ZOOMS = [0.5, 0.75, 1, 1.25, 1.5, 2];

export default function CanvasEditor({ envelopeId, documents, roles, readOnly }: Props) {
  const [docIndex, setDocIndex] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pdf, setPdf] = useState<PdfDoc | null>(null);
  const [rendered, setRendered] = useState({ width: 0, height: 0 });
  const [hist, setHist] = useState<HistoryState<Field[]>>(initHistory<Field[]>([]));
  const [selected, setSelected] = useState<string[]>([]);
  const [activeRole, setActiveRole] = useState(roles[0]?.roleId ?? '');
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [guides, setGuides] = useState<SnapGuide[]>([]);
  const [showExtended, setShowExtended] = useState(false);
  const clipboard = useRef<Field[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const loadedRef = useRef(false);
  /** What Firestore held at the last successful save; the delete diff is against this. */
  const fieldsAtLastSave = useRef<Field[]>([]);

  const document_ = documents[docIndex];
  const fields = hist.present;
  const pageSize = document_?.pageSizes[pageIndex] ?? { width: 612, height: 792 };

  useEffect(() => { if (!activeRole && roles[0]) setActiveRole(roles[0].roleId); }, [roles, activeRole]);

  // Load persisted fields once. After that the canvas is the source of truth for
  // this session; a live subscription would fight the drag state.
  useEffect(() => {
    const q = query(collection(db, 'envelopes', envelopeId, 'fields'), orderBy('order'));
    const unsub = onSnapshot(q, (snap) => {
      if (loadedRef.current) return;
      loadedRef.current = true;
      const loaded = snap.docs.map((d) => d.data() as Field);
      // Seed the deletion baseline HERE, at the moment we learn what exists.
      // Doing it in an effect keyed on a ref never re-runs, which left the
      // baseline empty and silently skipped deletes of pre-existing fields.
      fieldsAtLastSave.current = loaded;
      setHist(initHistory(loaded));
    });
    return unsub;
  }, [envelopeId]);

  useEffect(() => {
    let cancelled = false;
    if (!document_) return;
    setLoading(true);
    loadPdf(document_.storagePath)
      .then((d) => { if (!cancelled) { setPdf(d); setPageIndex(0); } })
      .catch(() => { if (!cancelled) setPdf(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [document_?.storagePath]);

  useEffect(() => {
    if (!pdf || !canvasRef.current) return;
    let cancelled = false;
    renderPage(pdf, pageIndex, canvasRef.current, zoom)
      .then((size) => { if (!cancelled) setRendered(size); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [pdf, pageIndex, zoom]);

  // Debounced persistence. Writing on every pointermove would be thousands of
  // Firestore writes per layout session.
  const saveTimer = useRef<number | null>(null);
  const persist = useCallback((next: Field[]) => {
    if (readOnly) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    setSaveState('saving');
    saveTimer.current = window.setTimeout(async () => {
      const batch = writeBatch(db);
      const col = collection(db, 'envelopes', envelopeId, 'fields');
      const seen = new Set(next.map((f) => f.id));
      next.forEach((f, i) => batch.set(doc(col, f.id), { ...f, order: i }));
      for (const f of fieldsAtLastSave.current) if (!seen.has(f.id)) batch.delete(doc(col, f.id));
      await batch.commit();
      fieldsAtLastSave.current = next;
      setSaveState('saved');
      window.setTimeout(() => setSaveState('idle'), 1500);
    }, 600);
  }, [envelopeId, readOnly]);

  const mutate = useCallback((fn: (f: Field[]) => Field[], label: string, key?: string) => {
    setHist((h) => {
      const next = apply(h, fn, label, key);
      if (next !== h) persist(next.present);
      return next;
    });
  }, [persist]);

  const addField = useCallback((type: FieldType, atX: number, atY: number) => {
    if (!document_ || !activeRole) return;
    const size = defaultSizeFor(type, pageSize);
    const rect = clampToPage({ x: atX - size.width / 2, y: atY - size.height / 2, ...size });
    const spec = FIELD_SPECS[type];
    const field: Field = {
      id: crypto.randomUUID().replace(/-/g, '').slice(0, 20),
      documentId: document_.id, pageIndex, type, roleId: activeRole,
      required: true, label: spec?.label ?? type, fontSize: 11,
      order: fields.length, ...rect,
    };
    mutate((f) => [...f, field], `add ${type}`);
    setSelected([field.id]);
  }, [document_, activeRole, pageIndex, pageSize, fields.length, mutate]);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('field-type') as FieldType;
    if (!type || !rendered.width) return;
    const box = (e.currentTarget as HTMLElement).getBoundingClientRect();
    addField(type, (e.clientX - box.left) / rendered.width, (e.clientY - box.top) / rendered.height);
  };

  /** Kept in a ref so the keyboard handler never closes over a stale page list. */
  const pageFieldsRef = useRef<Field[]>([]);

  const newId = () => crypto.randomUUID().replace(/-/g, '').slice(0, 20);

  const copySelected = useCallback(() => {
    clipboard.current = fields.filter((f) => selected.includes(f.id));
  }, [fields, selected]);

  /** Pastes with a small offset so the copy is visibly distinct from the original. */
  const paste = useCallback(() => {
    if (clipboard.current.length === 0 || !document_) return;
    const offset = 0.02;
    const created = clipboard.current.map((f, i) => ({
      ...f,
      id: newId(),
      documentId: document_.id,
      pageIndex,
      order: fields.length + i,
      ...clampToPage({ ...f, x: f.x + offset, y: f.y + offset }),
    }));
    mutate((all) => [...all, ...created], 'paste');
    setSelected(created.map((c) => c.id));
  }, [document_, pageIndex, fields.length, mutate]);

  const applyAlign = useCallback((mode: AlignMode) => {
    if (selected.length < 2) return;
    mutate((all) => {
      const chosen = all.filter((f) => selected.includes(f.id));
      const aligned = align(chosen, mode);
      const byId = new Map(aligned.map((a, i) => [chosen[i]!.id, a]));
      return all.map((f) => {
        const a = byId.get(f.id);
        return a ? { ...f, ...clampToPage(a) } : f;
      });
    }, `align ${mode}`);
  }, [selected, mutate]);

  const applyDistribute = useCallback((mode: DistributeMode) => {
    if (selected.length < 3) return;
    mutate((all) => {
      const chosen = all.filter((f) => selected.includes(f.id));
      const spread = distribute(chosen, mode);
      const byId = new Map(spread.map((a, i) => [chosen[i]!.id, a]));
      return all.map((f) => {
        const a = byId.get(f.id);
        return a ? { ...f, ...clampToPage(a) } : f;
      });
    }, `distribute ${mode}`);
  }, [selected, mutate]);

  // Arrow-key nudge: 1pt, or 10pt with shift. R-4.9.
  useEffect(() => {
    if (readOnly) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA') return;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        setHist((h) => { const n = e.shiftKey ? redo(h) : undo(h); if (n !== h) persist(n.present); return n; });
        return;
      }
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setSelected(pageFieldsRef.current.map((f) => f.id));
        return;
      }
      if (mod && e.key.toLowerCase() === 'c') { e.preventDefault(); copySelected(); return; }
      if (mod && e.key.toLowerCase() === 'v') { e.preventDefault(); paste(); return; }
      if (mod && e.key.toLowerCase() === 'd') {
        e.preventDefault(); copySelected(); paste(); return;
      }
      if (e.key === 'Escape') { setSelected([]); return; }
      if (selected.length === 0) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        mutate((f) => f.filter((x) => !selected.includes(x.id)), 'delete');
        setSelected([]);
        return;
      }
      const step = (e.shiftKey ? 10 : 1);
      const deltas: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step],
      };
      const d = deltas[e.key];
      if (!d) return;
      e.preventDefault();
      mutate((f) => f.map((x) => selected.includes(x.id)
        ? { ...x, ...clampToPage({ ...x, x: x.x + d[0] / pageSize.width, y: x.y + d[1] / pageSize.height }) }
        : x), 'nudge', `nudge:${selected.join(',')}`);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, pageSize, mutate, persist, readOnly, copySelected, paste]);

  const pageFields = useMemo(
    () => fields.filter((f) => f.documentId === document_?.id && f.pageIndex === pageIndex),
    [fields, document_?.id, pageIndex]
  );

  // Overlap warning only across DIFFERENT roles — two fields for the same signer
  // sitting close together is normal; two signers' fields on top of each other
  // means one of them cannot be filled. R-4.12.
  const overlapping = useMemo(() => {
    const bad = new Set<string>();
    for (let i = 0; i < pageFields.length; i++) {
      for (let j = i + 1; j < pageFields.length; j++) {
        const a = pageFields[i]!, b = pageFields[j]!;
        if (a.roleId !== b.roleId && overlaps(a, b)) { bad.add(a.id); bad.add(b.id); }
      }
    }
    return bad;
  }, [pageFields]);

  useEffect(() => { pageFieldsRef.current = pageFields; }, [pageFields]);

  const roleIndex = (roleId: string) => Math.max(0, roles.findIndex((r) => r.roleId === roleId));
  const sel = fields.find((f) => f.id === selected[0]);

  if (!document_) return <p className="muted">Upload a document to start placing fields.</p>;

  return (
    <div>
      <div className="row" style={{ gap: '.5rem', marginBottom: '.75rem' }}>
        {documents.length > 1 && (
          <select value={docIndex} onChange={(e) => setDocIndex(Number(e.target.value))}>
            {documents.map((d, i) => <option key={d.id} value={i}>{d.name}</option>)}
          </select>
        )}
        <div className="row" style={{ gap: '.35rem' }}>
          <button onClick={() => setPageIndex((p) => Math.max(0, p - 1))} disabled={pageIndex === 0}>‹</button>
          <span className="muted">Page {pageIndex + 1} of {document_.pageCount}</span>
          <button onClick={() => setPageIndex((p) => Math.min(document_.pageCount - 1, p + 1))}
                  disabled={pageIndex >= document_.pageCount - 1}>›</button>
        </div>
        <select value={zoom} onChange={(e) => setZoom(Number(e.target.value))} aria-label="Zoom">
          {ZOOMS.map((z) => <option key={z} value={z}>{Math.round(z * 100)}%</option>)}
        </select>
        <div className="row" style={{ gap: '.35rem' }}>
          <button onClick={() => setHist((h) => { const n = undo(h); if (n !== h) persist(n.present); return n; })}
                  disabled={!canUndo(hist)} title="Undo (Cmd+Z)">Undo</button>
          <button onClick={() => setHist((h) => { const n = redo(h); if (n !== h) persist(n.present); return n; })}
                  disabled={!canRedo(hist)} title="Redo (Cmd+Shift+Z)">Redo</button>
        </div>
        <span className="muted" style={{ minWidth: '4.5rem' }}>
          {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : ''}
        </span>
      </div>

      <div className="row" style={{ gap: '.3rem', marginBottom: '.75rem', justifyContent: 'flex-start' }}>
        <span className="muted" style={{ fontSize: '.78rem' }}>
          {selected.length} selected
        </span>
        {([['left', 'Left'], ['hcenter', 'Centre'], ['right', 'Right'],
           ['top', 'Top'], ['vcenter', 'Middle'], ['bottom', 'Bottom']] as [AlignMode, string][])
          .map(([m, label]) => (
            <button key={m} onClick={() => applyAlign(m)} disabled={selected.length < 2}
                    style={{ padding: '.3rem .5rem', minHeight: 34, fontSize: '.78rem' }}>
              {label}
            </button>
          ))}
        <button onClick={() => applyDistribute('horizontal')} disabled={selected.length < 3}
                style={{ padding: '.3rem .5rem', minHeight: 34, fontSize: '.78rem' }}
                title="Equalise horizontal gaps">Space H</button>
        <button onClick={() => applyDistribute('vertical')} disabled={selected.length < 3}
                style={{ padding: '.3rem .5rem', minHeight: 34, fontSize: '.78rem' }}
                title="Equalise vertical gaps">Space V</button>
      </div>

      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <aside style={{ width: 190, flexShrink: 0 }}>
          <h3 style={{ fontSize: '.85rem', margin: '0 0 .35rem' }}>Assign to</h3>
          <select value={activeRole} onChange={(e) => setActiveRole(e.target.value)} style={{ width: '100%', minHeight: 38 }}>
            {roles.map((r) => <option key={r.roleId} value={r.roleId}>{r.name}</option>)}
          </select>
          <div style={{ height: 4, background: roleColor(roleIndex(activeRole)), margin: '.4rem 0 1rem', borderRadius: 2 }} />

          <h3 style={{ fontSize: '.85rem', margin: '0 0 .35rem' }}>Fields</h3>
          <p className="muted" style={{ fontSize: '.75rem', margin: '0 0 .5rem' }}>Drag onto the page.</p>
          {(showExtended ? [...CORE_FIELD_TYPES, ...EXTENDED_FIELD_TYPES] : CORE_FIELD_TYPES).map((t) => (
            <div
              key={t} draggable={!readOnly}
              onDragStart={(e) => e.dataTransfer.setData('field-type', t)}
              onDoubleClick={() => addField(t, 0.5, 0.5)}
              title={FIELD_SPECS[t].hint}
              style={{
                border: '1px solid var(--line)', borderRadius: 6, padding: '.5rem .6rem',
                marginBottom: '.35rem', cursor: readOnly ? 'not-allowed' : 'grab',
                fontSize: '.85rem', background: 'var(--bg)',
              }}
            >
              {FIELD_SPECS[t].label}
            </div>
          ))}
          <button
            onClick={() => setShowExtended((s) => !s)}
            style={{ width: '100%', marginTop: '.35rem', fontSize: '.78rem', minHeight: 34 }}
          >
            {showExtended ? 'Fewer field types' : `More field types (${EXTENDED_FIELD_TYPES.length})`}
          </button>
        </aside>

        <div style={{ flex: 1, minWidth: 300, overflow: 'auto' }}>
          {loading && <p className="muted">Loading document…</p>}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onPointerDown={() => setSelected([])}
            style={{ position: 'relative', display: 'inline-block', lineHeight: 0, boxShadow: '0 1px 6px rgba(0,0,0,.18)' }}
          >
            <canvas ref={canvasRef} />
            {guides.map((g, i) => (
              <div
                key={`${g.axis}${g.at}${i}`}
                aria-hidden
                style={{
                  position: 'absolute', background: '#e0348b', pointerEvents: 'none',
                  ...(g.axis === 'x'
                    ? { left: g.at * rendered.width, top: 0, width: 1, height: rendered.height }
                    : { top: g.at * rendered.height, left: 0, height: 1, width: rendered.width }),
                }}
              />
            ))}
            {rendered.width > 0 && pageFields.map((f) => (
              <FieldBox
                key={f.id} field={f} color={roleColor(roleIndex(f.roleId))}
                selected={selected.includes(f.id)}
                renderedW={rendered.width} renderedH={rendered.height}
                overlapping={overlapping.has(f.id)}
                onSelect={(id, additive) =>
                  setSelected((s) => (additive ? (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]) : [id]))}
                onChange={(id, rect, key) => {
                  // Threshold is derived from pixels so the snap feels identical at
                  // every zoom. A fixed normalized threshold is hair-trigger when
                  // zoomed in and unreachable when zoomed out.
                  const thresholdX = 6 / Math.max(1, rendered.width);
                  const others = pageFieldsRef.current.filter((o) => o.id !== id);
                  const snapped = key.startsWith('move')
                    ? snap(rect, others, thresholdX)
                    : { rect, guides: [] };
                  setGuides(snapped.guides.slice(-2));
                  mutate((all) => all.map((x) => (x.id === id ? { ...x, ...snapped.rect } : x)), 'move', key);
                }}
                onCommit={() => { setGuides([]); setHist((h) => commit(h)); }}
              />
            ))}
          </div>
        </div>

        {sel && (
          <aside style={{ width: 210, flexShrink: 0, borderLeft: '1px solid var(--line)', paddingLeft: '1rem' }}>
            <h3 style={{ fontSize: '.85rem', marginTop: 0 }}>{FIELD_SPECS[sel.type]?.label ?? sel.type}</h3>
            <p className="muted" style={{ fontSize: '.75rem' }}>
              {FIELD_SPECS[sel.type]?.hint}
            </p>
            <label style={{ display: 'block', fontSize: '.8rem', marginBottom: '.5rem' }}>
              Label
              <input
                value={sel.label} maxLength={120}
                onChange={(e) => mutate((f) => f.map((x) => x.id === sel.id ? { ...x, label: e.target.value } : x), 'label', `label:${sel.id}`)}
                style={{ width: '100%', padding: '.35rem', minHeight: 36 }}
              />
            </label>
            <label style={{ display: 'block', fontSize: '.8rem', marginBottom: '.5rem' }}>
              Assigned to
              <select
                value={sel.roleId}
                onChange={(e) => mutate((f) => f.map((x) => x.id === sel.id ? { ...x, roleId: e.target.value } : x), 'assign')}
                style={{ width: '100%', minHeight: 36 }}
              >
                {roles.map((r) => <option key={r.roleId} value={r.roleId}>{r.name}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', gap: '.4rem', alignItems: 'center', fontSize: '.8rem', marginBottom: '.5rem' }}>
              <input
                type="checkbox" checked={sel.required}
                onChange={(e) => mutate((f) => f.map((x) => x.id === sel.id ? { ...x, required: e.target.checked } : x), 'required')}
              />
              Required
            </label>
            <label style={{ display: 'block', fontSize: '.8rem', marginBottom: '.75rem' }}>
              Font size: {sel.fontSize}pt
              <input
                type="range" min={6} max={24} value={sel.fontSize}
                onChange={(e) => mutate((f) => f.map((x) => x.id === sel.id ? { ...x, fontSize: Number(e.target.value) } : x), 'fontSize', `font:${sel.id}`)}
                style={{ width: '100%' }}
              />
            </label>
            {(sel.type === 'text' || sel.type === 'title' || sel.type === 'company') && (
              <>
                <label style={{ display: 'block', fontSize: '.8rem', marginBottom: '.5rem' }}>
                  Accepts
                  <select
                    value={sel.validation ?? 'any'}
                    onChange={(e) => mutate((f) => f.map((x) => x.id === sel.id
                      ? { ...x, validation: e.target.value as ValidationKind } : x), 'validation')}
                    style={{ width: '100%', minHeight: 36 }}
                  >
                    {VALIDATION_KINDS.map((k) => (
                      <option key={k} value={k}>{VALIDATION_LABELS[k]}</option>
                    ))}
                  </select>
                </label>
                {sel.validation === 'regex' && (
                  <label style={{ display: 'block', fontSize: '.8rem', marginBottom: '.5rem' }}>
                    Pattern
                    <input
                      value={sel.validationPattern ?? ''} placeholder="^[A-Z]{2}-\\d{3}$"
                      onChange={(e) => mutate((f) => f.map((x) => x.id === sel.id
                        ? { ...x, validationPattern: e.target.value } : x), 'pattern', `pat:${sel.id}`)}
                      style={{ width: '100%', padding: '.35rem', minHeight: 36, fontFamily: 'monospace' }}
                    />
                    <span className="muted" style={{ fontSize: '.7rem' }}>
                      An invalid pattern is ignored rather than trapping the signer.
                    </span>
                  </label>
                )}
              </>
            )}

            {(sel.type === 'radio' || sel.type === 'dropdown') && (
              <div style={{ marginBottom: '.5rem' }}>
                <span style={{ fontSize: '.8rem' }}>Options</span>
                {(sel.options ?? []).map((o, i) => (
                  <div key={i} className="row" style={{ gap: '.25rem', marginTop: '.25rem' }}>
                    <input
                      value={o.label}
                      onChange={(e) => mutate((f) => f.map((x) => x.id === sel.id
                        ? { ...x, options: (x.options ?? []).map((oo, j) =>
                            j === i ? { value: e.target.value, label: e.target.value } : oo) }
                        : x), 'option', `opt:${sel.id}:${i}`)}
                      style={{ flex: 1, padding: '.3rem', minHeight: 34 }}
                    />
                    <button
                      onClick={() => mutate((f) => f.map((x) => x.id === sel.id
                        ? { ...x, options: (x.options ?? []).filter((_, j) => j !== i) } : x), 'option')}
                      style={{ padding: '.2rem .45rem', minHeight: 34 }}
                    >×</button>
                  </div>
                ))}
                <button
                  onClick={() => mutate((f) => f.map((x) => x.id === sel.id
                    ? { ...x, options: [...(x.options ?? []), { value: 'Option', label: 'Option' }] } : x), 'option')}
                  style={{ marginTop: '.35rem', fontSize: '.78rem', minHeight: 34 }}
                >Add option</button>
              </div>
            )}

            {sel.type === 'radio' && (
              <label style={{ display: 'block', fontSize: '.8rem', marginBottom: '.5rem' }}>
                Group name
                <input
                  value={sel.groupId ?? ''} placeholder="e.g. payment_terms"
                  onChange={(e) => mutate((f) => f.map((x) => x.id === sel.id
                    ? { ...x, groupId: e.target.value } : x), 'group', `grp:${sel.id}`)}
                  style={{ width: '100%', padding: '.35rem', minHeight: 36 }}
                />
                <span className="muted" style={{ fontSize: '.7rem' }}>
                  Buttons sharing a group name are mutually exclusive.
                </span>
              </label>
            )}

            {sel.type === 'note' && (
              <label style={{ display: 'block', fontSize: '.8rem', marginBottom: '.5rem' }}>
                Note text
                <textarea
                  value={sel.noteText ?? ''} rows={3} maxLength={1000}
                  onChange={(e) => mutate((f) => f.map((x) => x.id === sel.id
                    ? { ...x, noteText: e.target.value } : x), 'note', `note:${sel.id}`)}
                  style={{ width: '100%', padding: '.35rem' }}
                />
              </label>
            )}

            <button
              onClick={() => { mutate((f) => f.filter((x) => x.id !== sel.id), 'delete'); setSelected([]); }}
              style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
            >
              Delete field
            </button>
          </aside>
        )}
      </div>

      {overlapping.size > 0 && (
        <div className="alert" style={{ marginTop: '1rem' }}>
          Two signers’ fields overlap on this page. Whoever signs second may not be able to reach theirs.
        </div>
      )}
      <p className="muted" style={{ marginTop: '1rem', fontSize: '.8rem' }}>
        Drag to move · corner to resize · Shift-click for multi-select · arrows nudge 1pt,
        Shift+arrow 10pt · Cmd+C/V copy-paste · Cmd+D duplicate · Cmd+A select all ·
        Esc deselect · Delete removes · Cmd+Z undo
      </p>
    </div>
  );
}
